import { Injectable } from '@angular/core';
import { LegalDocument } from '@shared/entities/legalDocument';
import { ApiService } from '@shared/services';
import { Observable, catchError, map, of, tap } from 'rxjs';

const LOG_PREFIX = '[Api][LegalDocument] ';

@Injectable({
  providedIn: 'root'
})
export class LegalDocumentService extends ApiService<LegalDocument> {
  constructor() {
    const masterUrl = '/legal-documents';
    const detailUrl = '/legal-documents';
    super(LegalDocument, masterUrl, detailUrl);
  }

  getAll(): Observable<LegalDocument[]> {
    this.logger.debug(LOG_PREFIX + 'getAll');
    return this.paginate(LegalDocument, 1, 50).pipe(
      map((paginate) => paginate.data || []),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'getAll error', error);
        return of([]);
      })
    );
  }

  find(id: string): Observable<LegalDocument | null> {
    this.logger.debug(LOG_PREFIX + `find with id: ${id}`);
    // The API exposes no /legal-documents/{id} route, so a single document is read by
    // filtering the list on id (same approach as BannerService).
    this.setUrlParameter('id', id);
    return this.paginate(LegalDocument, 1, 1).pipe(
      map((paginate) => paginate.data?.[0] || null),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'find error', error);
        return of(null);
      })
    );
  }

  update(id: string, htmlContent: string | undefined, pdf?: File): Observable<LegalDocument> {
    this.logger.debug(LOG_PREFIX + `update with id: ${id}`, { hasPdf: !!pdf });

    const legalDocument = new LegalDocument();
    legalDocument.id = id;
    legalDocument.htmlContent = htmlContent;

    const formData = new FormData();
    formData.append('request', JSON.stringify(legalDocument));
    if (pdf) {
      formData.append('pdf', pdf);
    }

    return this.putMultipartData<LegalDocument>(`/legal-documents?id=${id}`, formData, LegalDocument).pipe(
      tap((result) => this.logger.debug(LOG_PREFIX + 'update success', result)),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'update error', error);
        throw error;
      })
    );
  }
}
